import { useState, useMemo, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Menu,
  Checkbox,
  CircularProgress,
  Alert,
  Skeleton,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  Share as ShareIcon,
  Description as DescriptionIcon,
  ArrowDropDown as ArrowDropDownIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { MainLayout } from '../layouts/MainLayout';
import { projectService, MA_METHOD_LABELS, PROJECT_STATUS_LABELS } from '../services/api/projectService';
import { checkItemService, type CategoryInfo } from '../services/api/checkItemService';
import type {
  ProjectDetail,
  CheckItem,
  CheckStatus,
  RiskCategory,
  ProjectStatus,
  RiskSummary,
} from '../types';

// Risk category configuration
const riskCategoryConfig: Record<RiskCategory, { name: string; totalItems: number }> = {
  legal: { name: '法的リスク', totalItems: 42 },
  economic: { name: '経済的リスク', totalItems: 28 },
  financial: { name: '財政的リスク', totalItems: 35 },
  international: { name: '国際的リスク', totalItems: 24 },
  domestic_political: { name: '国内政治的リスク', totalItems: 18 },
  reputation: { name: '対世論リスク', totalItems: 30 },
  employee: { name: '従業員リスク', totalItems: 25 },
  labor_union: { name: '労働組合リスク', totalItems: 20 },
};

const statusConfig: Record<ProjectStatus, { label: string; color: 'info' | 'warning' | 'success' | 'default' }> = {
  planning: { label: '検討中', color: 'info' },
  in_progress: { label: '進行中', color: 'warning' },
  completed: { label: '完了', color: 'success' },
  cancelled: { label: '中止', color: 'default' },
};

const checkStatusOptions: { value: CheckStatus; label: string }[] = [
  { value: 'unchecked', label: '未確認' },
  { value: 'completed', label: '完了' },
  { value: 'not_applicable', label: '該当なし' },
  { value: 'needs_review', label: '要確認' },
];

export function ProjectDetailPage() {
  const { id: projectId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [reportMenuAnchor, setReportMenuAnchor] = useState<null | HTMLElement>(null);
  const [expandedCategories, setExpandedCategories] = useState<RiskCategory[]>(['legal']);

  // API data states
  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [categories, setCategories] = useState<CategoryInfo[]>([]);
  const [checkItems, setCheckItems] = useState<CheckItem[]>([]);
  const [checkResults, setCheckResults] = useState<Record<string, { status: CheckStatus; memo: string }>>({});

  // Loading states
  const [isLoadingProject, setIsLoadingProject] = useState(true);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [isLoadingCheckItems, setIsLoadingCheckItems] = useState(true);
  const [updatingItems, setUpdatingItems] = useState<Set<string>>(new Set());

  // Error states
  const [projectError, setProjectError] = useState<string | null>(null);
  const [categoriesError, setCategoriesError] = useState<string | null>(null);
  const [checkItemsError, setCheckItemsError] = useState<string | null>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);

  // Fetch project detail
  useEffect(() => {
    if (!projectId) return;

    const fetchProject = async () => {
      setIsLoadingProject(true);
      setProjectError(null);
      try {
        const projectData = await projectService.getProject(projectId);
        setProject(projectData);

        // Initialize check results from project data
        const resultsMap: Record<string, { status: CheckStatus; memo: string }> = {};
        projectData.checkResults.forEach((result) => {
          resultsMap[result.checkItemId] = {
            status: result.status,
            memo: result.memo || '',
          };
        });
        setCheckResults(resultsMap);
      } catch (error) {
        setProjectError(error instanceof Error ? error.message : '案件の取得に失敗しました');
      } finally {
        setIsLoadingProject(false);
      }
    };

    fetchProject();
  }, [projectId]);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      setIsLoadingCategories(true);
      setCategoriesError(null);
      try {
        const categoriesData = await checkItemService.getCategories();
        setCategories(categoriesData);
      } catch (error) {
        setCategoriesError(error instanceof Error ? error.message : 'カテゴリの取得に失敗しました');
      } finally {
        setIsLoadingCategories(false);
      }
    };

    fetchCategories();
  }, []);

  // Fetch all check items
  useEffect(() => {
    const fetchCheckItems = async () => {
      setIsLoadingCheckItems(true);
      setCheckItemsError(null);
      try {
        const items = await checkItemService.getCheckItems();
        setCheckItems(items);
      } catch (error) {
        setCheckItemsError(error instanceof Error ? error.message : 'チェック項目の取得に失敗しました');
      } finally {
        setIsLoadingCheckItems(false);
      }
    };

    fetchCheckItems();
  }, []);

  // Group check items by category
  const checkItemsByCategory = useMemo(() => {
    const grouped: Record<string, CheckItem[]> = {};
    checkItems.forEach((item) => {
      if (!grouped[item.categoryId]) {
        grouped[item.categoryId] = [];
      }
      grouped[item.categoryId].push(item);
    });
    // Sort items by order within each category
    Object.keys(grouped).forEach((categoryId) => {
      grouped[categoryId].sort((a, b) => a.order - b.order);
    });
    return grouped;
  }, [checkItems]);

  // Group check items by risk category for display
  const checkItemsByRiskCategory = useMemo(() => {
    const grouped: Record<RiskCategory, CheckItem[]> = {
      legal: [],
      economic: [],
      financial: [],
      international: [],
      domestic_political: [],
      reputation: [],
      employee: [],
      labor_union: [],
    };

    categories.forEach((cat) => {
      const items = checkItemsByCategory[cat.id] || [];
      grouped[cat.riskCategory] = [...grouped[cat.riskCategory], ...items];
    });

    return grouped;
  }, [categories, checkItemsByCategory]);

  // Calculate risk summaries
  const riskSummaries: RiskSummary[] = useMemo(() => {
    return Object.entries(riskCategoryConfig).map(([category, config]) => {
      const items = checkItemsByRiskCategory[category as RiskCategory] || [];
      const completed = items.filter((item) => {
        const result = checkResults[item.id];
        return result?.status === 'completed' || result?.status === 'not_applicable';
      }).length;

      const total = items.length > 0 ? items.length : config.totalItems;
      const percentage = total > 0 ? Math.floor((completed / total) * 100) : 0;

      return {
        category: category as RiskCategory,
        categoryName: config.name,
        total,
        completed,
        percentage,
      };
    });
  }, [checkResults, checkItemsByRiskCategory]);

  const totalProgress = useMemo(() => {
    const total = riskSummaries.reduce((sum, s) => sum + s.total, 0);
    const completed = riskSummaries.reduce((sum, s) => sum + s.completed, 0);
    return { total, completed, percentage: total > 0 ? Math.floor((completed / total) * 100) : 0 };
  }, [riskSummaries]);

  const handleCategoryToggle = (category: RiskCategory) => {
    setExpandedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category]
    );
  };

  // Debounced update function
  const updateCheckResult = useCallback(
    async (itemId: string, status: CheckStatus, memo: string) => {
      if (!projectId) return;

      setUpdatingItems((prev) => new Set(prev).add(itemId));
      setUpdateError(null);

      try {
        await projectService.updateCheckResult(projectId, itemId, { status, memo });
      } catch (error) {
        setUpdateError(error instanceof Error ? error.message : 'チェック結果の更新に失敗しました');
        // Revert local state on error - fetch fresh data
        const projectData = await projectService.getProject(projectId);
        const resultsMap: Record<string, { status: CheckStatus; memo: string }> = {};
        projectData.checkResults.forEach((result) => {
          resultsMap[result.checkItemId] = {
            status: result.status,
            memo: result.memo || '',
          };
        });
        setCheckResults(resultsMap);
      } finally {
        setUpdatingItems((prev) => {
          const newSet = new Set(prev);
          newSet.delete(itemId);
          return newSet;
        });
      }
    },
    [projectId]
  );

  const handleStatusChange = (itemId: string, status: CheckStatus) => {
    const currentMemo = checkResults[itemId]?.memo || '';
    setCheckResults((prev) => ({
      ...prev,
      [itemId]: { ...prev[itemId], status, memo: currentMemo },
    }));
    updateCheckResult(itemId, status, currentMemo);
  };

  const handleMemoChange = (itemId: string, memo: string) => {
    const currentStatus = checkResults[itemId]?.status || 'unchecked';
    setCheckResults((prev) => ({
      ...prev,
      [itemId]: { ...prev[itemId], memo, status: currentStatus },
    }));
  };

  const handleMemoBlur = (itemId: string) => {
    const result = checkResults[itemId];
    if (result) {
      updateCheckResult(itemId, result.status, result.memo);
    }
  };

  const getStatusChipProps = (status: CheckStatus) => {
    switch (status) {
      case 'completed':
        return { label: '完了', color: 'success' as const };
      case 'needs_review':
        return { label: '要確認', color: 'warning' as const };
      case 'not_applicable':
        return { label: '該当なし', color: 'default' as const };
      default:
        return null;
    }
  };

  const isLoading = isLoadingProject || isLoadingCategories || isLoadingCheckItems;
  const hasError = projectError || categoriesError || checkItemsError;

  // Loading skeleton
  if (isLoading) {
    return (
      <MainLayout>
        <Box>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/projects')} sx={{ mb: 2 }}>
            案件一覧へ戻る
          </Button>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Skeleton variant="text" width={300} height={40} />
            <Skeleton variant="rounded" width={80} height={24} />
          </Box>

          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            <Skeleton variant="text" width={150} height={32} sx={{ mb: 2 }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
              {[1, 2, 3, 4].map((i) => (
                <Box key={i}>
                  <Skeleton variant="text" width={80} height={20} />
                  <Skeleton variant="text" width={150} height={24} />
                </Box>
              ))}
            </Box>
          </Paper>

          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            <Skeleton variant="text" width={180} height={32} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={100} />
          </Paper>

          <Paper elevation={1} sx={{ p: 3 }}>
            <Skeleton variant="text" width={150} height={32} sx={{ mb: 2 }} />
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rectangular" height={60} sx={{ mb: 1 }} />
            ))}
          </Paper>
        </Box>
      </MainLayout>
    );
  }

  // Error display
  if (hasError) {
    return (
      <MainLayout>
        <Box>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/projects')} sx={{ mb: 2 }}>
            案件一覧へ戻る
          </Button>

          <Alert severity="error" sx={{ mb: 2 }}>
            {projectError || categoriesError || checkItemsError}
          </Alert>

          <Button variant="contained" onClick={() => window.location.reload()}>
            再読み込み
          </Button>
        </Box>
      </MainLayout>
    );
  }

  if (!project) {
    return (
      <MainLayout>
        <Box>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/projects')} sx={{ mb: 2 }}>
            案件一覧へ戻る
          </Button>

          <Alert severity="warning">案件が見つかりませんでした</Alert>
        </Box>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Box>
        {/* Update error notification */}
        {updateError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUpdateError(null)}>
            {updateError}
          </Alert>
        )}

        {/* Back button */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/projects')}
          sx={{ mb: 2 }}
        >
          案件一覧へ戻る
        </Button>

        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h5" component="h1" fontWeight={500}>
              {project.name}
            </Typography>
            <Chip
              label={statusConfig[project.status].label}
              color={statusConfig[project.status].color}
              size="small"
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" startIcon={<ShareIcon />} size="small">
              共有
            </Button>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              size="small"
              onClick={() => navigate(`/projects/${projectId}/edit`)}
            >
              編集
            </Button>
            <Button
              variant="contained"
              startIcon={<DescriptionIcon />}
              endIcon={<ArrowDropDownIcon />}
              onClick={(e) => setReportMenuAnchor(e.currentTarget)}
            >
              レポート出力
            </Button>
            <Menu
              anchorEl={reportMenuAnchor}
              open={Boolean(reportMenuAnchor)}
              onClose={() => setReportMenuAnchor(null)}
            >
              <MenuItem onClick={() => setReportMenuAnchor(null)}>
                <PdfIcon sx={{ mr: 1 }} /> PDFで出力
              </MenuItem>
              <MenuItem onClick={() => setReportMenuAnchor(null)}>
                <ExcelIcon sx={{ mr: 1 }} /> Excelで出力
              </MenuItem>
            </Menu>
          </Box>
        </Box>

        {/* Project Info Card */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            案件基本情報
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">対象企業名</Typography>
              <Typography variant="body1">{project.targetCompany}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">M&A手法</Typography>
              <Typography variant="body1">{MA_METHOD_LABELS[project.maMethod]}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">ステータス</Typography>
              <Typography variant="body1">{PROJECT_STATUS_LABELS[project.status]}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">作成日</Typography>
              <Typography variant="body1">{new Date(project.createdAt).toLocaleDateString('ja-JP')}</Typography>
            </Box>
            <Box sx={{ gridColumn: { sm: 'span 2' } }}>
              <Typography variant="caption" color="text.secondary">概要</Typography>
              <Typography variant="body1">{project.description || '-'}</Typography>
            </Box>
          </Box>
        </Paper>

        {/* Risk Summary Card */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            リスク評価サマリー
          </Typography>

          {/* Overall Progress */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body1" fontWeight={500}>全体進捗</Typography>
              <Typography variant="body2" color="text.secondary">
                {totalProgress.completed} / {totalProgress.total} 項目完了（{totalProgress.percentage}%）
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={totalProgress.percentage}
              sx={{ height: 8, borderRadius: 1 }}
            />
          </Box>

          {/* Category Progress Grid */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
            {riskSummaries.map((summary) => (
              <Box key={summary.category}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2">{summary.categoryName}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {summary.completed}/{summary.total}（{summary.percentage}%）
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={summary.percentage}
                  sx={{ height: 6, borderRadius: 1 }}
                />
              </Box>
            ))}
          </Box>
        </Paper>

        {/* Checklist Section */}
        <Paper elevation={1} sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            チェックリスト
          </Typography>

          {Object.entries(riskCategoryConfig).map(([category, config]) => {
            const items = checkItemsByRiskCategory[category as RiskCategory] || [];
            const completedCount = items.filter((item) => {
              const result = checkResults[item.id];
              return result?.status === 'completed' || result?.status === 'not_applicable';
            }).length;

            return (
              <Accordion
                key={category}
                expanded={expandedCategories.includes(category as RiskCategory)}
                onChange={() => handleCategoryToggle(category as RiskCategory)}
                sx={{ mb: 1 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography fontWeight={500}>{config.name}</Typography>
                    <Chip
                      label={`${completedCount}/${items.length}`}
                      color="info"
                      size="small"
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {items.length === 0 ? (
                    <Typography color="text.secondary" sx={{ py: 2 }}>
                      このカテゴリにはチェック項目がありません
                    </Typography>
                  ) : (
                    items.map((item) => {
                      const result = checkResults[item.id];
                      const status = result?.status || 'unchecked';
                      const memo = result?.memo || '';
                      const statusChip = getStatusChipProps(status);
                      const isNotApplicable = status === 'not_applicable';
                      const isUpdating = updatingItems.has(item.id);

                      return (
                        <Box
                          key={item.id}
                          sx={{
                            py: 1.5,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            '&:last-child': { borderBottom: 'none' },
                            opacity: isUpdating ? 0.7 : 1,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Checkbox
                              checked={status === 'completed'}
                              disabled={isNotApplicable || isUpdating}
                              onChange={(e) =>
                                handleStatusChange(item.id, e.target.checked ? 'completed' : 'unchecked')
                              }
                              color="success"
                            />
                            <Typography
                              sx={{
                                textDecoration: isNotApplicable ? 'line-through' : 'none',
                                color: isNotApplicable ? 'text.disabled' : 'text.primary',
                              }}
                            >
                              {item.name}
                            </Typography>
                            {statusChip && (
                              <Chip
                                label={statusChip.label}
                                color={statusChip.color}
                                size="small"
                                sx={{ ml: 1 }}
                              />
                            )}
                            {isUpdating && (
                              <CircularProgress size={16} sx={{ ml: 1 }} />
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', gap: 2, ml: 5 }}>
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                              <Select
                                value={status}
                                onChange={(e) => handleStatusChange(item.id, e.target.value as CheckStatus)}
                                disabled={isUpdating}
                              >
                                {checkStatusOptions.map((opt) => (
                                  <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <TextField
                              size="small"
                              placeholder="メモを入力..."
                              value={memo}
                              onChange={(e) => handleMemoChange(item.id, e.target.value)}
                              onBlur={() => handleMemoBlur(item.id)}
                              disabled={isUpdating}
                              sx={{ flex: 1 }}
                              multiline
                              maxRows={3}
                            />
                          </Box>
                        </Box>
                      );
                    })
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Paper>
      </Box>
    </MainLayout>
  );
}
